import React from 'react';
import { Table, Group, Text, ActionIcon, Menu, Badge } from '@mantine/core';
import {
  IconFile,
  IconDots,
  IconPencil,
  IconTrash,
  IconDownload,
  IconHistory,
  IconRocket,
  IconArrowBounce,
} from '@tabler/icons-react';
import { DBObjects } from '@/store/store_types';
import { isAudioFile } from '@/utils/file_utils';
import { PlayIcon } from '@/modules/audio_player/audio_player_module';
import { useModals } from '@/modules/music_sniper/components/modals/modals_provider';
import { useCurrentUser } from '@/modules/music_sniper/hooks/useCurrentUser';
import { useModule } from '@/hooks/useModule';
import { formatTimeAgo } from '../../new_dashboard/utils/timeFormatting';
import { useAction } from '@/store/kysely_module';
import { useIsMobile } from '@/utils/user_agent';

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

type FileInfo = DBObjects['File'] & { num_revisions: number; duration: string | null };

type Props = {
  fileInfos: FileInfo[];
  onItemClick: (fileInfo: DBObjects['File']) => void;
  refresh: () => void;
  makeLauncherFile?: (relativePath: string) => void;
  makeBounceFile?: (relativePath: string) => void;
};

export const FileTableWithActions: React.FC<Props> = ({
  fileInfos,
  onItemClick,
  refresh,
  makeLauncherFile,
  makeBounceFile,
}) => {
  const isMobile = useIsMobile();
  const modals = useModals();
  const currentUser = useCurrentUser();
  const msFilesModule = useModule('MSFiles');

  const updateFileName = useAction('updateFileName');
  const deleteFile = useAction('deleteFile');

  const sortedFiles = [...fileInfos].sort((a, b) =>
    a.created_at > b.created_at ? -1 : 1
  );

  const handleRename = (fileInfo: FileInfo) => {
    const lastPeriodIndex = fileInfo.name.lastIndexOf('.');
    const fileName = fileInfo.name.substring(0, lastPeriodIndex);
    const suffix = fileInfo.name.substring(lastPeriodIndex);

    modals.openModal('TextInputModal', {
      title: 'Rename file',
      inputName: 'File name',
      initialValue: fileName,
      suffix,
      onSubmit: async (fileName) => {
        const fullName = `${fileName}${suffix}`;
        await updateFileName(
          { file_id: fileInfo.id, new_name: fullName },
          { workspace_id: fileInfo.workspace_id }
        );
        refresh();
      },
    });
  };

  const handleDelete = (fileInfo: FileInfo) => {
    modals.openModal('ConfirmationModal', {
      title: 'Delete this file?',
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        await deleteFile(
          { file_id: fileInfo.id },
          { workspace_id: fileInfo.workspace_id }
        );
        refresh();
      },
    });
  };

  const handleDownload = async (fileInfo: FileInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    const fileSrc = await msFilesModule.client().getFileSrc(fileInfo);
    const link = document.createElement('a');
    link.download = fileInfo.name;
    link.href = fileSrc;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileIcon = (file: FileInfo) => {
    if (isAudioFile(file.name)) {
      return <PlayIcon fileInfo={file} size={16} />;
    }
    return <IconFile size={16} style={{ opacity: 0.7 }} />;
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table striped highlightOnHover withRowBorders>
        {!isMobile && (
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: '40px' }}></Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th style={{ width: '100px' }}>Duration</Table.Th>
              <Table.Th style={{ width: '120px' }}>Created</Table.Th>
              <Table.Th style={{ width: '80px' }}>Revisions</Table.Th>
              <Table.Th style={{ width: '200px' }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
        )}
        <Table.Tbody>
          {sortedFiles.map((file) => (
            <Table.Tr
              key={file.id}
              style={{ cursor: 'pointer' }}
              onClick={() => onItemClick(file)}
            >
              <Table.Td style={{ padding: isMobile ? '6px' : '8px' }} onClick={(e) => e.stopPropagation()}>
                {getFileIcon(file)}
              </Table.Td>
              <Table.Td style={{ padding: isMobile ? '6px' : '8px' }}>
                <Text
                  size="sm"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {file.name}
                </Text>
              </Table.Td>
              {!isMobile && (
                <>
                  <Table.Td style={{ padding: '8px' }}>
                    {file.duration && isAudioFile(file.name) && (
                      <Text size="sm" c="dimmed">
                        {formatDuration(Number(file.duration))}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td style={{ padding: '8px' }}>
                    <Text size="sm" c="dimmed">
                      {formatTimeAgo(file.created_at, isMobile)}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ padding: '8px' }}>
                    {file.num_revisions > 1 && (
                      <Group gap="xs">
                        <IconHistory size={14} />
                        <Text size="sm">{file.num_revisions}</Text>
                      </Group>
                    )}
                  </Table.Td>
                </>
              )}
              <Table.Td
                style={{ padding: isMobile ? '6px' : '8px' }}
                onClick={(e) => e.stopPropagation()}
              >
                <Group gap="xs" wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={(e) => handleDownload(file, e)}
                    title="Download"
                  >
                    <IconDownload size={14} />
                  </ActionIcon>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="sm">
                        <IconDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPencil size={14} />}
                        onClick={() => handleRename(file)}
                      >
                        Rename
                      </Menu.Item>
                      {makeLauncherFile && (
                        <Menu.Item
                          leftSection={<IconRocket size={14} />}
                          onClick={() => makeLauncherFile(file.relative_path)}
                        >
                          Assign as Launcher File
                        </Menu.Item>
                      )}
                      {makeBounceFile && isAudioFile(file.name) && (
                        <Menu.Item
                          leftSection={<IconArrowBounce size={14} />}
                          onClick={() => makeBounceFile(file.relative_path)}
                        >
                          Assign as Bounce File
                        </Menu.Item>
                      )}
                      {file.created_by === currentUser?.id && (
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDelete(file)}
                        >
                          Delete
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
};
